// Who the header shows (FR-024, FR-027; R-9; data-model §5). The Akaiva logo is never an option.
import { effectiveRole } from "@/session/access";
import { useSession } from "@/session/hooks";
import { usePlatform } from "@/session/platform";
import type { Platform, SchoolIdentity, SessionState } from "@/session/types";

import { resolveAdminSchool, useAdminSchool } from "./adminSchoolSelection";
import { useCurrentSchool } from "./CurrentSchoolProvider";

export type HeaderIdentity =
  | { kind: "school"; school: SchoolIdentity; label: "School admin" | null }
  | { kind: "product" };

export function headerIdentity(
  session: SessionState,
  currentSchool: SchoolIdentity | null,
  platform: Platform,
  /** 006: the school-admin school remembered on this device (several schools only). */
  rememberedAdminSchoolId: string | null = null,
): HeaderIdentity {
  if (session.status !== "signedIn") return { kind: "product" };
  const role = effectiveRole(session, platform);
  if (role === "SCHOOL_ADMIN") {
    // 006 (FR-001, US6-2): the resolved school; product text until a school is chosen.
    const r = resolveAdminSchool(
      session.schoolAdminSchools,
      rememberedAdminSchoolId,
    );
    if (r.kind === "single" || r.kind === "chosen") {
      return { kind: "school", school: r.school, label: "School admin" };
    }
    return { kind: "product" };
  }
  if (role === "PARENT" && currentSchool) {
    return { kind: "school", school: currentSchool, label: null };
  }
  return { kind: "product" };
}

export function useHeaderIdentity(): HeaderIdentity {
  const session = useSession();
  const [currentSchool] = useCurrentSchool();
  const { resolution } = useAdminSchool();
  const remembered = resolution.kind === "chosen" ? resolution.school.id : null;
  return headerIdentity(session, currentSchool, usePlatform(), remembered);
}
